import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Button,
  Typography,
  Tabs,
  Tab,
  Grid,
} from '@mui/material';
import FunctionsIcon from '@mui/icons-material/Functions';

interface ScientificKeyboardProps {
  onInsertSymbol: (symbol: string) => void;
  selectedSubject?: string;
}

// Symbol categories
const MATH_SYMBOLS = {
  basic: [
    { label: '+', value: '+' },
    { label: '-', value: '-' },
    { label: '×', value: '×' },
    { label: '÷', value: '÷' },
    { label: '=', value: '=' },
    { label: '≠', value: '≠' },
    { label: '<', value: '<' },
    { label: '>', value: '>' },
    { label: '≤', value: '≤' },
    { label: '≥', value: '≥' },
    { label: '±', value: '±' },
    { label: '∓', value: '∓' },
  ],
  algebra: [
    { label: 'x²', value: '^2' },
    { label: 'xⁿ', value: '^n' },
    { label: '√', value: '√' },
    { label: '∛', value: '∛' },
    { label: '∜', value: '∜' },
    { label: 'x₁', value: '_1' },
    { label: 'xₙ', value: '_n' },
    { label: '∑', value: '∑' },
    { label: '∏', value: '∏' },
    { label: '∫', value: '∫' },
    { label: '∂', value: '∂' },
    { label: '∞', value: '∞' },
  ],
  geometry: [
    { label: '°', value: '°' },
    { label: '∠', value: '∠' },
    { label: '⊥', value: '⊥' },
    { label: '∥', value: '∥' },
    { label: '△', value: '△' },
    { label: '○', value: '○' },
    { label: '□', value: '□' },
    { label: 'π', value: 'π' },
    { label: '∼', value: '∼' },
    { label: '≅', value: '≅' },
    { label: '∝', value: '∝' },
    { label: '⊕', value: '⊕' },
  ],
  calculus: [
    { label: 'lim', value: 'lim' },
    { label: 'd/dx', value: 'd/dx' },
    { label: '∫', value: '∫' },
    { label: '∮', value: '∮' },
    { label: '∇', value: '∇' },
    { label: 'Δ', value: 'Δ' },
    { label: '∂', value: '∂' },
    { label: '∞', value: '∞' },
    { label: '→', value: '→' },
    { label: '∴', value: '∴' },
    { label: '∵', value: '∵' },
    { label: '⇒', value: '⇒' },
  ],
  greek: [
    { label: 'α', value: 'α' },
    { label: 'β', value: 'β' },
    { label: 'γ', value: 'γ' },
    { label: 'δ', value: 'δ' },
    { label: 'ε', value: 'ε' },
    { label: 'θ', value: 'θ' },
    { label: 'λ', value: 'λ' },
    { label: 'μ', value: 'μ' },
    { label: 'π', value: 'π' },
    { label: 'σ', value: 'σ' },
    { label: 'φ', value: 'φ' },
    { label: 'ω', value: 'ω' },
  ],
  fractions: [
    { label: '½', value: '½' },
    { label: '⅓', value: '⅓' },
    { label: '¼', value: '¼' },
    { label: '⅕', value: '⅕' },
    { label: '⅙', value: '⅙' },
    { label: '⅛', value: '⅛' },
    { label: '⅔', value: '⅔' },
    { label: '¾', value: '¾' },
    { label: '⅖', value: '⅖' },
    { label: '⅗', value: '⅗' },
    { label: '⅘', value: '⅘' },
    { label: '⅚', value: '⅚' },
  ],
};

const PHYSICS_SYMBOLS = [
  { label: 'μ₀', value: 'μ₀' },
  { label: 'ε₀', value: 'ε₀' },
  { label: 'ℏ', value: 'ℏ' },
  { label: 'Ω', value: 'Ω' },
  { label: 'λ', value: 'λ' },
  { label: 'ν', value: 'ν' },
  { label: '∆E', value: '∆E' },
  { label: 'F⃗', value: 'F⃗' },
];

const CHEMISTRY_SYMBOLS = [
  { label: '→', value: '→' },
  { label: '⇌', value: '⇌' },
  { label: '↓', value: '↓' },
  { label: '↑', value: '↑' },
  { label: '⊕', value: '⊕' },
  { label: '⊖', value: '⊖' },
  { label: 'Δ', value: 'Δ' },
  { label: '⁺', value: '⁺' },
  { label: '⁻', value: '⁻' },
  { label: '²⁺', value: '²⁺' },
  { label: '³⁺', value: '³⁺' },
  { label: '²⁻', value: '²⁻' },
];

const ScientificKeyboard: React.FC<ScientificKeyboardProps> = ({
  onInsertSymbol,
  selectedSubject = '',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSymbolClick = (symbol: string) => {
    onInsertSymbol(symbol);
  };

  const open = Boolean(anchorEl);

  // Determine which categories to show based on subject
  const getCategories = () => {
    const categories = [
      { label: 'Basic', symbols: MATH_SYMBOLS.basic },
      { label: 'Algebra', symbols: MATH_SYMBOLS.algebra },
      { label: 'Geometry', symbols: MATH_SYMBOLS.geometry },
      { label: 'Calculus', symbols: MATH_SYMBOLS.calculus },
      { label: 'Greek', symbols: MATH_SYMBOLS.greek },
      { label: 'Fractions', symbols: MATH_SYMBOLS.fractions },
    ];

    if (selectedSubject?.toLowerCase().includes('physics')) {
      categories.push({ label: 'Physics', symbols: PHYSICS_SYMBOLS });
    }

    if (selectedSubject?.toLowerCase().includes('chemistry')) {
      categories.push({ label: 'Chemistry', symbols: CHEMISTRY_SYMBOLS });
    }

    return categories;
  };

  const categories = getCategories();

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          bgcolor: open ? '#E3F2FD' : 'transparent',
          '&:hover': { bgcolor: '#E3F2FD' },
        }}
        title="Scientific Keyboard"
      >
        <FunctionsIcon fontSize="small" />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: { xs: '320px', sm: '400px', md: '480px' },
            maxHeight: '400px',
            p: 1,
          },
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 600, color: '#2c3e50', px: 1 }}
          >
            Scientific Keyboard
          </Typography>

          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 1,
              '& .MuiTab-root': {
                minWidth: 'auto',
                px: 1.5,
                py: 0.75,
                fontSize: { xs: '11px', md: '12px' },
                textTransform: 'none',
              },
            }}
          >
            {categories.map((cat, index) => (
              <Tab key={cat.label} label={cat.label} />
            ))}
          </Tabs>

          <Box sx={{ p: 1, maxHeight: '280px', overflowY: 'auto' }}>
            {categories.map((category, catIndex) => (
              <Box
                key={category.label}
                sx={{ display: tabValue === catIndex ? 'block' : 'none' }}
              >
                <Grid container spacing={0.5}>
                  {category.symbols.map((symbol, index) => (
                    <Grid item xs={3} sm={2.4} md={2} key={index}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleSymbolClick(symbol.value)}
                        sx={{
                          minWidth: 'auto',
                          width: '100%',
                          height: { xs: '36px', md: '40px' },
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 500,
                          borderColor: '#e0e0e0',
                          color: '#2c3e50',
                          '&:hover': {
                            bgcolor: '#E3F2FD',
                            borderColor: '#2196F3',
                          },
                        }}
                      >
                        {symbol.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
              Click symbol to insert
            </Typography>
            <Button size="small" onClick={handleClose} sx={{ fontSize: '11px' }}>
              Close
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ScientificKeyboard;